/**
 * Whether this site is publishing dishes whose allergens are not yet verified.
 *
 * `CATALOGUE_ALLOW_UNVERIFIED` is a test-site switch (Barna, 10 October 2026).
 * The database holds it and `menu_context` reports it as
 * `catalogue_unverified`, so the customer page reads it out of the context it
 * already has. The pages that do not load the menu — the return page, the
 * survey, the console, the print views — ask once here and share the answer.
 *
 * Fail towards the banner: when the question cannot be answered and the site
 * is not production, the notice shows. A test-system notice shown once too
 * often costs nothing; one missing while unverified dishes are on the page is
 * the thing this switch must never allow.
 */

import { type PublicConfig, isProduction } from './env.ts'
import { type MenuContext, readMenuContext } from './menuContext.ts'

export const TEST_SYSTEM_NOTICE = {
  hu: 'Tesztrendszer — az allergének ellenőrzés alatt állnak. Itt leadott rendelés nem valódi megrendelés.',
  en: 'Test system — allergens are being verified. An order placed here is not a real order.',
} as const

export const catalogueUnverified = (context: Pick<MenuContext, 'catalogue_unverified'> | null | undefined): boolean =>
  context?.catalogue_unverified === true

let asked: Promise<boolean> | null = null

export function loadCatalogueUnverified(
  config: PublicConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  if (asked === null) {
    asked = readMenuContext(config, {}, fetchImpl)
      .then((context) => catalogueUnverified(context))
      .catch(() => !isProduction(config))
  }
  return asked
}

/** For tests: forget the shared answer. */
export function resetCatalogueModeForTest(): void {
  asked = null
}
